import {
  BoundDateField,
  BoundMultiSelectField,
  BoundSelectField,
  BoundSwitchField,
  BoundTextField,
  Css,
  FormLines,
  Icon,
  IconButton,
  StaticField,
  TabsWithContent,
  TabWithContent,
  TextField,
} from "@homebound/beam";
import { Observer } from "mobx-react";
import { useCallback, useEffect, useState } from "react";
import { AssetGallery } from "src/components/assetGallery/AssetGallery";
import { useSaveFollowersMutation } from "src/components/comments/CommentFeed.generated";
import {
  DocumentDetailDocumentTypeFragment,
  DocumentEditorDetailFragment,
  InternalUserDetailFragment,
  NamedDevelopmentCohortsProjectsFragment,
} from "src/fragments/circularFragments.generated";
import {
  DocumentAttachmentPlace,
  DocumentType,
  Maybe,
  PotentialOperation2,
  ProjectDocumentAssociationInput,
  SaveDocumentInput,
} from "src/generated/graphqlTypes";
import { disableBasedOnPotentialOperation } from "src/routes/components/PotentialOperationsUtils";
import { DocumentScheduleTasks } from "src/routes/projects/documents/DocumentScheduleTasks";
import { DocumentsPageMode, useMaybeNavigateToNewDraft } from "src/routes/projects/documents/DocumentsPage";
import {
  useDocumentEditorQuery,
  useSaveDocumentsMutation,
  useScheduleTasksForDocumentParentQuery,
  useToggleDocumentAsFavoritedMutation,
} from "src/routes/projects/documents/DocumentsPage.generated";
import { bytesToSize, safeEntries } from "src/utils";
import { ObjectConfig, useFormState } from "src/utils/formState";
import { queryResult } from "src/utils/queryResult";
import { Temporal } from "temporal-polyfill";
import { StringParam, useQueryParams } from "use-query-params";
import { AssetPreview } from "../assets/AssetPreview";
import { BoundAssociatedProjectsTreeSelect, ProjectWithChildren } from "./BoundAssociatedProjectsTreeSelect";
import { DocumentVersionManager } from "./DocumentVersionManager";

export type DocumentEditorProps = {
  documentId: string;
  documentTypes: DocumentDetailDocumentTypeFragment[];
  groupBy?: string;
  versionId?: string;
};

export function DocumentEditor(props: DocumentEditorProps) {
  const { documentId, documentTypes, groupBy, versionId } = props;
  const query = useDocumentEditorQuery({ variables: { documentId, versionId } });

  return queryResult(query, ({ document, internalUsers }) => (
    <DocumentEditorView
      document={document}
      documentTypes={documentTypes}
      internalUsers={internalUsers}
      groupBy={groupBy}
      versionId={versionId}
    />
  ));
}

type DocumentEditorViewProps = Omit<DocumentEditorProps, "documentId"> & {
  document: DocumentEditorDetailFragment;
  documentTypes: DocumentDetailDocumentTypeFragment[];
  internalUsers: InternalUserDetailFragment[];
  versionId?: string;
};

function DocumentEditorView(props: DocumentEditorViewProps) {
  const { document, documentTypes, groupBy, internalUsers, versionId } = props;
  const { parent, canEdit } = document;
  const { __typename: parentType } = parent;
  const parentProject =
    parentType !== "PlanPackage" && parentType !== "Development" && "childrenRecursive" in parent ? parent : undefined;
  const mode =
    parentType === "PlanPackage"
      ? DocumentsPageMode.ReadyPlan
      : parentType === "Development"
        ? DocumentsPageMode.Development
        : parentProject?.childrenRecursive.nonEmpty
          ? DocumentsPageMode.Hierarchy
          : DocumentsPageMode.Project;
  // If the document is editable, then we should display all our document types.  Otherwise, lock down the field.
  const displayedDocTypes = canEdit.allowed ? documentTypes.sortByKey("name") : [document.documentType];
  const scheduleTasksQuery = useScheduleTasksForDocumentParentQuery({
    variables: { parentId: document.parent.id },
    skip: mode === DocumentsPageMode.ReadyPlan,
  });
  const [saveDocumentsMutation] = useSaveDocumentsMutation();
  const [toggleDocumentAsFavoritedMutation] = useToggleDocumentAsFavoritedMutation();

  const maybeNavigateToNewDraft = useMaybeNavigateToNewDraft();

  // If the document is on a development, get its parent's cohorts and projects
  const development = document.parent.__typename === "Development" ? document.parent : undefined;

  const onSave = useCallback(
    async (input: SaveDocumentInput) => {
      const { data } = await saveDocumentsMutation({
        variables: { input: [input] },
        refetchQueries: ["DocumentsPage", "DocumentsForParent"],
      });
      const parents = data?.saveDocuments.documents.map((d) => d.parent) ?? [];
      maybeNavigateToNewDraft(versionId, parents, { groupBy, documentId: document.id });
    },
    [document, groupBy, maybeNavigateToNewDraft, saveDocumentsMutation, versionId],
  );

  const onArchive = useCallback(
    async (document: DocumentEditorDetailFragment) => {
      const { data } = await saveDocumentsMutation({
        variables: { input: [{ id: document.id, deleted: !document.isDeleted }] },
        refetchQueries: ["DocumentsPage", "DocumentsForParent"],
      });
      const parents = data?.saveDocuments.documents.map((d) => d.parent) ?? [];
      maybeNavigateToNewDraft(versionId, parents, { groupBy, documentId: document.id });
    },
    [groupBy, maybeNavigateToNewDraft, saveDocumentsMutation, versionId],
  );

  const onFavorite = useCallback(
    (document: DocumentEditorDetailFragment) =>
      toggleDocumentAsFavoritedMutation({ variables: { input: { documentId: document.id } } }),
    [toggleDocumentAsFavoritedMutation],
  );

  const scheduleTasks = scheduleTasksQuery.data?.scheduleTasks ?? [];
  const [, setQueryParams] = useQueryParams({ documentId: StringParam });

  const relationshipsTab: TabWithContent = {
    name: "Relationships",
    value: "relationships",
    render: () => <DocumentScheduleTasks document={document} scheduleTasks={scheduleTasks} onSave={onSave} />,
  };

  const tabs: TabWithContent[] = [
    {
      name: "Details",
      value: "details",
      render: () => (
        <Observer>
          {() => (
            <DocumentForm
              document={document}
              internalUsers={internalUsers}
              canEdit={canEdit}
              displayedDocTypes={displayedDocTypes}
              docTypeCode={document.documentType.code}
              onSave={onSave}
              development={development}
              parentProject={parentProject}
            />
          )}
        </Observer>
      ),
    },
    ...(mode !== DocumentsPageMode.ReadyPlan ? [relationshipsTab] : []),
    { name: "History", value: "history", render: () => <DocumentVersionManager document={document} /> },
  ];

  const [tab, setTab] = useState<string>(tabs[0].value);

  return (
    <div css={Css.df.fdc.gap1.pb2.h100.oa.$}>
      <div css={Css.p3.pb0.$}>
        <header css={Css.df.jcsb.aifs.mb1.gap1.$}>
          <div css={Css.df.aic.jcfe.gap1.$}>
            <IconButton
              icon={document.isFavorited ? "starFilled" : "star"}
              tooltip={document.isFavorited ? "Remove from favorites" : "Add to favorites"}
              onClick={() => onFavorite(document)}
            />
            <IconButton onClick={document.asset.attachmentUrl} icon="download" />
            <IconButton
              onClick={() => onArchive(document)}
              icon={document.isDeleted ? "unarchive" : "archive"}
              tooltip={document.isDeleted ? "Unarchive" : "Archive"}
              disabled={disableBasedOnPotentialOperation(document.canArchive!)}
            />
          </div>
          <div css={Css.df.jcsb.aic.gap1.$}>
            {/* TODO: add up and down navigation to the header of documents*/}
            <IconButton data-testid="closePane" icon="x" onClick={() => setQueryParams({ documentId: undefined })} />
          </div>
        </header>
        <div css={Css.lg.$}>{document.displayName}</div>
      </div>

      <div css={Css.p3.pt0.$}>
        <div css={Css.mb2.$}>
          <AssetGallery assets={[document.asset]}>
            {(openGallery) => <AssetPreview asset={document.asset} onClick={() => openGallery(document.asset)} />}
          </AssetGallery>
        </div>

        <TabsWithContent selected={tab} tabs={tabs} onChange={(value) => setTab(value)} />
      </div>
    </div>
  );
}

type FormValue = Pick<
  SaveDocumentInput,
  "id" | "name" | "homeownerVisible" | "category" | "projectDocumentAssociations" | "attachOn"
> & { followers: Maybe<Array<string>>; issuanceDate: Maybe<Temporal.PlainDate>; documentTypeCode: Maybe<DocumentType> };

const projectDocumentAssociationConfig: ObjectConfig<ProjectDocumentAssociationInput> = {
  projectId: { type: "value", isIdKey: true },
  delete: { type: "value", isDeleteKey: true },
};

const formConfig: ObjectConfig<FormValue> = {
  id: { type: "value" },
  name: { type: "value" },
  documentTypeCode: { type: "value" },
  homeownerVisible: { type: "value" },
  followers: { type: "value" },
  attachOn: { type: "value" },
  issuanceDate: { type: "value" },
  projectDocumentAssociations: { type: "list", config: projectDocumentAssociationConfig },
};

function mapToForm(document: DocumentEditorDetailFragment): FormValue {
  const {
    id,
    documentType,
    displayName: name,
    homeownerVisible,
    followers,
    issuanceDate,
    associatedProjects,
    category,
    attachOn,
  } = document;
  return {
    id,
    name,
    category,
    attachOn,
    homeownerVisible,
    followers: followers.map(({ id }) => id),
    documentTypeCode: documentType.code,
    issuanceDate,
    projectDocumentAssociations: associatedProjects.map(({ id }) => ({ projectId: id })),
  };
}

function mapToInput(formInput: FormValue): SaveDocumentInput {
  return {
    id: formInput.id,
    documentType: formInput.documentTypeCode as DocumentType,
    name: formInput.name,
    homeownerVisible: formInput.homeownerVisible,
    issuanceDate: formInput?.issuanceDate,
    projectDocumentAssociations: formInput.projectDocumentAssociations,
    attachOn: formInput.attachOn,
  };
}

type DocumentFormProps = {
  document: DocumentEditorDetailFragment;
  internalUsers: InternalUserDetailFragment[];
  canEdit: PotentialOperation2;
  displayedDocTypes: DocumentDetailDocumentTypeFragment[] | undefined;
  docTypeCode: DocumentType;
  onSave: (input: SaveDocumentInput) => void;
  development?: NamedDevelopmentCohortsProjectsFragment;
  parentProject?: ProjectWithChildren;
};

function DocumentForm(opts: DocumentFormProps) {
  const { document, internalUsers, canEdit, displayedDocTypes, docTypeCode, development, parentProject, onSave } = opts;
  const [saveFollowersMutation] = useSaveFollowersMutation();
  const saveFollowers = async (followerIds: string[]) =>
    saveFollowersMutation({ variables: { input: { followableId: document.id, followerIds } } });
  const formState = useFormState({
    config: formConfig,
    init: { input: document, map: mapToForm },
    autoSave: async (state) => {
      if (state.changedValue.followers) {
        await saveFollowers(state.changedValue.followers);
      }
      const { projectDocumentAssociations } = state;
      const { id, followers, ...other } = state.changedValue;
      // List fields aren't registered by changedValue, so we have to manually add dirty pda inputs in
      if (projectDocumentAssociations.dirty) {
        other.projectDocumentAssociations = projectDocumentAssociations.value;
      }
      if (Object.keys(other).nonEmpty) {
        onSave(mapToInput({ ...state.changedValue, projectDocumentAssociations: other.projectDocumentAssociations }));
      }
    },
    readOnly: !canEdit.allowed,
  });

  const { sizeInBytes, createdBy } = document;
  const filteredDisplayedDocTypes = displayedDocTypes?.filter(
    ({ code }) => code !== DocumentType.ArchitectureAndEngineering,
  );

  // For some reason, the updated value in the multi-select and issuance date of an updated doc would persist when switching, so this prevents that
  useEffect(() => {
    if (canEdit.allowed) {
      formState.followers.set(document.followers.map(({ id }) => id));
      formState.issuanceDate.set(document.issuanceDate);
      formState.attachOn.set(document.attachOn);
    }
  }, [document, formState, canEdit]);

  const disabled = disableBasedOnPotentialOperation(canEdit);

  return (
    <FormLines width="full">
      <div css={Css.df.jcsb.gap3.$}>
        <BoundTextField label="Name" field={formState.name} disabled={disabled} readOnly={false} />
        <div css={Css.w25.$}>
          <TextField
            xss={Css.lh("36px").$}
            label="Version"
            value={document.asset.version}
            onChange={() => {}}
            readOnly
          />
        </div>
      </div>
      <div>
        <BoundSelectField
          label="Type"
          field={formState.documentTypeCode}
          // deprecating A&E document type, so only show the value if it's already been selected, otherwise filter it out
          options={
            (formState.documentTypeCode.value === DocumentType.ArchitectureAndEngineering
              ? displayedDocTypes
              : filteredDisplayedDocTypes) || []
          }
          getOptionLabel={(option) => option.name}
          getOptionValue={(option) => option.code}
          disabledOptions={[DocumentType.General]}
          disabled={disabled}
          readOnly={false}
        />
        {docTypeCode === DocumentType.General && (
          <div css={Css.df.aic.bcYellow50.sm.gray700.mt1.br8.$}>
            <Icon icon="error" />
            <div css={Css.plPx(4).$}>Select a type to keep this project organized!</div>
          </div>
        )}
      </div>
      <StaticField label="Category" value={document.categoryDetail?.name ?? ""} />
      {(development || parentProject?.childrenRecursive.nonEmpty) && (
        <BoundAssociatedProjectsTreeSelect
          development={development}
          parentProject={parentProject}
          readOnly={!canEdit.allowed}
          field={formState.projectDocumentAssociations}
        />
      )}
      {canEdit.allowed && (
        <BoundMultiSelectField
          label="Watchers"
          field={formState.followers}
          options={internalUsers}
          fieldDecoration={({ avatar, name }) => (
            <img src={avatar} alt={name} css={Css.br100.wPx(24).hPx(24).objectCover.oh.$} />
          )}
          getOptionMenuLabel={({ avatar, name }) => (
            <div css={Css.df.aifs.gap1.$}>
              <img src={avatar} alt={name} css={Css.br100.wPx(24).hPx(24).objectCover.oh.$} />
              <span>{name}</span>
            </div>
          )}
        />
      )}
      <BoundDateField field={formState.issuanceDate} readOnly={formState.readOnly} />
      {document.asset.contentType === "application/pdf" && (
        <BoundMultiSelectField
          label="Attach to"
          field={formState.attachOn}
          options={safeEntries(DocumentAttachmentPlace)}
          getOptionLabel={([name]) => name}
          getOptionValue={([, code]) => code}
        />
      )}
      {formState.readOnly ? (
        <StaticField label="Homeowner Viewable" value={formState.homeownerVisible.value ? "Yes" : "No"} />
      ) : (
        <BoundSwitchField label="Homeowner Viewable" labelStyle="inline" field={formState.homeownerVisible} />
      )}
      <div css={Css.df.jcsb.mb2.$}>
        <StaticField label="Created By" value={createdBy.name} />
        <StaticField label="File Status" value={document.isDeleted ? "Archived" : "Active"} />
        <StaticField label="File Size" value={bytesToSize(sizeInBytes)} />
      </div>
    </FormLines>
  );
}
